import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';

import { PageTransition } from 'pages/common/components/PageTransition/PageTransition';

import {
  GtmDataLayerEvent,
  trackGtmDataLayerEvent,
} from '../../../../utils/analytics';
import { useFunnelStorage } from '../../storage/Storage';
import { generateSafeUrl, isUsUser, STEP_KEY } from '../../utils';

import { SUB_ROUTES, SUBSTEPS } from './constants';
import Parent from './Parent';
import AltPaymentPlans from './substeps/AltPaymentPlans/AltPaymentPlans';
import AmericaInsurancePlan from './substeps/Details/AmericaInsurancePlan';
import Details from './substeps/Details/Details';
import NotEligible from './substeps/NotEligible/NotEligible';
import SocialAssistance from './substeps/SocialAssistance/SocialAssistance';

const Insurance = ({ position, moveToNextStep }) => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const location = useLocation();
  const storage = useFunnelStorage();

  const safeUrl = generateSafeUrl(url);

  // LOGIC TO RENDER A SUBSTEP ON INITIAL RENDER.
  // If none of this proves true, defaults to initial insurance step
  useEffect(() => {
    trackGtmDataLayerEvent(GtmDataLayerEvent.SEARCH_QUESTION_INSURANCE_REACHED);

    const previousInsuranceAnswers = storage.get(STEP_KEY.INSURANCE);
    const userUsedBrowserButtons = history.action === 'POP';

    // 1. Determine what state user is in (if in one)
    const userDoesNotHaveInsurance =
      previousInsuranceAnswers
      && previousInsuranceAnswers.hasInsurance === 'No';

    const userHasInsurance =
      get(previousInsuranceAnswers, 'hasInsurance') === 'Yes';

    // 2. Trigger substep based on state, as long as user is NOT using browser buttons
    if (!userUsedBrowserButtons) {
      if (userDoesNotHaveInsurance) {
        // if US user, go to alt payment plans
        if (isUsUser(storage)) {
          setSubStep(SUBSTEPS.ALT_PAYMENT_PLANS);
        } else {
          setSubStep(SUBSTEPS.HAS_SOCIAL_ASSISTANCE);
        }
      }

      if (userHasInsurance) {
        setSubStep(SUBSTEPS.DETAILS);
      }
    }
  }, []);

  const setSubStep = useCallback((subStep) => {
    const subRoute = SUB_ROUTES[subStep];

    if (subRoute) {
      history.push(`${safeUrl}${subRoute.path}/`);
    }
  }, [history, safeUrl]);

  return (
    <PageTransition id={location.pathname.split('/')[3]}>
      <Switch location={location}>
        <Route path={`${safeUrl}${SUB_ROUTES[SUBSTEPS.DETAILS].path}`}>
          <Details
            position={position}
            moveToNextStep={moveToNextStep}
            setSubStep={setSubStep}
          />
        </Route>

        <Route path={`${safeUrl}${SUB_ROUTES[SUBSTEPS.HAS_SOCIAL_ASSISTANCE].path}`}>
          <SocialAssistance
            position={position}
            moveToNextStep={moveToNextStep}
            setSubStep={setSubStep}
          />
        </Route>

        <Route path={`${safeUrl}${SUB_ROUTES[SUBSTEPS.ALT_PAYMENT_PLANS].path}`}>
          <AltPaymentPlans
            position={position}
            moveToNextStep={moveToNextStep}
            setSubStep={setSubStep}
          />
        </Route>

        <Route path={`${safeUrl}${SUB_ROUTES[SUBSTEPS.PLANS].path}`}>
          <AmericaInsurancePlan
            position={position}
            moveToNextStep={moveToNextStep}
            setSubStep={setSubStep}
          />
        </Route>

        <Route path={`${safeUrl}${SUB_ROUTES[SUBSTEPS.NOT_ELIGIBLE].path}`}>
          <NotEligible position={position} />
        </Route>

        <Route path={safeUrl}>
          <Parent position={position} setSubStep={setSubStep} />
        </Route>
      </Switch>
    </PageTransition>
  );
};

Insurance.defaultProps = {
  position: '',
};

Insurance.propTypes = {
  position: PropTypes.string,
  moveToNextStep: PropTypes.func.isRequired,
};

export default Insurance;
